// CSS
import './Results.css';
// React hooks
import { useState, useEffect, useRef } from 'react';
import React from 'react';
import { useNavigate } from 'react-router-dom';
// Skybase hooks
import { useData } from '../../context/DataContext.js';
import { useQuery } from '../../context/QueryContext.js';
import { useAuth as useAuthContext } from '../../context/AuthContext.js';
// Skybase components
import Sidebar from '../../components/Sidebar/Sidebar.js';                                                                      // Sidebar
import FullResultDoc from '../../components/ResultInstance/FullResult/FullResultDoc/FullResultDoc';                             // FullResultDoc
import FullScreenModal from '../../components/Modals/FullScreenModal/FullScreenModal.js';                                       // FullScreenModal
import DocumentPreviewModal from '../../components/Modals/FullScreenModal/DocumentPreviewModal/DocumentPreviewModal.js';        // DocumentPreviewModal
import ResultIndexOverlay from './Components/ResultIndexOverlay/ResultIndexOverlay.js';                                         // ResultIndexOverlay
import ResultIndexCounterOverlay from './Components/ResultIndexCounterOverlay/ResultIndexCounterOverlay.js';                    // ResultIndexCounterOverlay
import LoadingScreenOverlay from '../../components/LoadingScreenOverlay/LoadingScreenOverlay.js';                               // LoadingScreenOverlay
import InformationalPopover from '../../components/Popovers/InformationalPopover/InformationalPopover.js';                      // InformationalPopover
// External hooks/components
import CountUp from 'react-countup';                                                                                            // number animations
import { useAuth as useClerkAuth } from '@clerk/react';                                                                         // user authe.
import { useMediaQuery } from 'react-responsive';                                                                               // responsive CSS
import { Virtuoso } from 'react-virtuoso';                                                                                      // Virtualization
// Media assets
import SearchIconTransparent from '../../assets/StandardIcons/search-icon-transparent.png';
// health day



// =======
// Results – represents the '/results' Product page.
// =======
function Results() {

    // destructure global context
    const { activeEngineJson, isLoading, enableLoadingScreen, disableLoadingScreen } = useData();
    const { activeQuery, rankedQueryResults, processSearchQuery, isKeywordIntent, isSearching } = useQuery();
    const { checkIsSubscribed, activeUserJson } = useAuthContext();
    const { isLoading: isClerkLoading, getToken } = useClerkAuth();

    // init hooks
    const navigate = useNavigate('');

    // state vars + localStorage caching
    // 'resultsFilter'
    const [resultsFilter, setResultsFilter] = useState(() => {
        
        // get localStorage cache, otherwise default: 'all'
        const saved = localStorage.getItem("resultsFilter");
        return saved ? JSON.parse(saved) : 'all';   // String (enum)
    })
    useEffect(() => {
        localStorage.setItem("resultsFilter", JSON.stringify(resultsFilter));
    }, [resultsFilter]);
    // 'viewedResults'
    const [viewedResults, setViewedResults] = useState(() => {

        // get localStorage cache, otherwise default: []
        const saved = localStorage.getItem("viewedResults");
        return saved ? JSON.parse(saved) : [];      // UUID[]
    });
    useEffect(() => {
        localStorage.setItem("viewedResults", JSON.stringify(viewedResults));
    }, [viewedResults]);

    // onDismount() --> delete '/result' page specific localStorage cache items
    useEffect(() => {

        // onDismount() behavior
        return () => {

            // delete '/result' page specific localStorage cache items
            localStorage.removeItem("resultsFilter");
            localStorage.removeItem("viewedResults");
        };
    }, []);

    // responsive CSS
    const isDesktop = useMediaQuery({ minWidth: 1025 });
    

    // ================
    // onMount Behavior
    // ================
    // onMount --> check for valid Skybase Stripe subscription
    const [isInformationalPopoverOpen, setIsInformationalPopoverOpen] = useState(false);
    useEffect(() => {
        
        // Async. fn that prevents CurrentUsers without an active subscription from interacting with this page.
        const checkSubscription = async () => {
            // Don't perform a subscription-check if the Clerk User hasn't loaded yet.
            if (isClerkLoading) return;

            // Does CurrentUser have an active subscription?
            const subscribed = await checkIsSubscribed();

            // If NO: Display InformationalPopover.
            if (!subscribed) { setIsInformationalPopoverOpen(true); }
        };

        // Run the async. subscription-check
        checkSubscription();

        // Disable any loading screens.
        // disableLoadingScreen();

        // Edge case: "bfcache === true" --> Forcibly reload the page to re-run the async. subscription-check
        const handlePageShow = (event) => {
            if (event.persisted) { window.location.reload(); }
        };
        window.addEventListener("pageshow", handlePageShow); // "pageShow" event listener

        // Edge case: No <Clerk JWT> --> Immediately redirect user from this page.
        checkForValidClerkTokenOtherwiseRedirect();

        // Remove listeners onDismount (prevent memory overflow)
        return () => { window.removeEventListener("pageshow", handlePageShow); };
    }, [isClerkLoading])

    
    // =======================
    // LOADING SCREEN BEHAVIOR – control whether the loading screen is ON or OFF
    // =======================
    const [isFull, setIsFull] = useState(false);
    useEffect(() => {

        // Only disable the loading screen IFF NOT actively searching.
        if (!isSearching) {
            disableLoadingScreen();
        } else {
            setIsFull(true);
            return;
        }

    }, [isSearching])

    
    // ==========================================
    // checkForValidClerkTokenOtherwiseRedirect()
    // ==========================================
    // Checks for a valid clerk token. Forcibly re-directs the user to '/login' if not.
    async function checkForValidClerkTokenOtherwiseRedirect() {
        let token = await getToken();
        if (!token) { navigate('../login') };
    }
    // onChange ('activeUserJson') --> immediately check again for valid subscription
    useEffect(() => {
        checkForValidClerkTokenOtherwiseRedirect();
    }, [activeUserJson]);


    // ======================
    // DOCUMENT PREVIEW MODAL
    // ======================
    const [isDocumentPreviewModalOpen, setIsDocumentPreviewModalOpen] = useState(false);    // Default: FALSE (closed)
    const [targetDocumentUUID, setTargetDocumentUUID] = useState(null);                     // Default: NULL (no Document target)
    // openDocumentPreviewModal(documentUUID: String) – opens the DocumentPreviewModal with a target Document UUID
    const openDocumentPreviewModal = (documentUUID) => {
        
        // Condition: Add this documentUUID to 'viewedResults' (UUID[]) IFF not already added
        if (!viewedResults.includes(documentUUID)) { setViewedResults(prev => [...prev, documentUUID]); }

        // open this 'documentUUID'
        handleEntityOpen(documentUUID);
        setTargetDocumentUUID(documentUUID);
        setIsDocumentPreviewModalOpen(true);
    }
    // closeDocumentPreviewModal() – closes the DocumentPreviewModal
    const handleDocumentClose = () => { setIsDocumentPreviewModalOpen(false) };

    
    // =======================
    // NUM RESULTS CALCULATION (metadata)
    // =======================
    // vars
    const numTotalResults = rankedQueryResults.length;
    const numKeywordExactResults = rankedQueryResults.filter(
        result => result.tags.includes("title-keyword-exact")
    ).length;
    const numSemanticResults = rankedQueryResults.filter(
        result => result.tags.includes("title-semantic-high") || result.tags.includes("subsection-semantic-high")
    ).length;

    
    // ===================
    // HANDLE SEARCH QUERY
    // ===================
    const originalSearchQuery = activeQuery;                // The 'activeQuery' value from dataContext.js
    const [searchQuery, setSearchQuery] = useState('');     // Default: Empty String
    // handleFormChange(e: Event) –> Track search query input changes
    const inputRef = useRef(null);
    const handleFormChange = (e) => {
        
        // ensure the HTML input is focused
        inputRef.current?.focus();

        // continuously update the new search query
        setSearchQuery(e.target.value);
    }

    
    // =========================
    // HANDLE REPEATER EMPTINESS
    // =========================
    const [isRepeaterEmpty, setIsRepeaterEmpty] = useState(false);  // Default: FALSE (!isRepeaterEmpty)
    // onChange ('resultsFilter', 'numTotalResults') –> immediately check if the repeater is Empty
    useEffect(() => {
        if (resultsFilter === 'all' && rankedQueryResults.length === 0) {
            setIsRepeaterEmpty(true);
        } else if (resultsFilter === 'keyword' && numKeywordExactResults === 0) {
            setIsRepeaterEmpty(true);
        } else if (resultsFilter === 'semantic' && numSemanticResults === 0) {
            setIsRepeaterEmpty(true);
        } else {
            setIsRepeaterEmpty(false);
        }

    }, [resultsFilter, numTotalResults]);


    // ============================
    // SEARCH INPUT FORM SUBMISSION
    // ============================
    const [estimatedSearchTimeMilliseconds, setEstimatedSearchTimeMilliseconds] = useState(12000); // estimated search time –> TARGET: 3s (3000s)
    // onChange ('isKeywordIntent') -> adjust the estimated search time (ms)
    useEffect(() => {
        if (isKeywordIntent) {
            setEstimatedSearchTimeMilliseconds(600);
        } else {
            setEstimatedSearchTimeMilliseconds(12000);
        };
    }, [isKeywordIntent]);
    const delay = (ms) => new Promise(resolve => setTimeout(resolve, ms)); // for sleeping
    
    
    // ==============================
    // RE-PROCESSING NEW SEARCH QUERY
    // ==============================
    // track useRef() to prevent /results page jumpscare
    const isPageActiveRef = useRef(true);
    useEffect(() => {
        // The component/page isActive while mounted.
        isPageActiveRef.current = true;

        // The component/page !isActive when CurrentUser navigates away.
        return () => {
            // Component is unmounted
            isPageActiveRef.current = false;

            // Immediately disable the loading screen.
            disableLoadingScreen();
        };
    }, []);
    // handleFormSubmit(e: Event) –> re-processes a new, non-empty and non-duplicate search query
    const handleFormSubmit = async (e) => {

        setIsFull(null);

        e.preventDefault(); // prevent default HTML form behavior

        // extract the final search query
        const finalSearchQuery = searchQuery

        // Edge case: NEVER process empty search queries
        if (!finalSearchQuery) { return };

        // Edge case: Never re-process the exact same search query (NOTE: Trims leading and trailing white-spaces, and converts to lowercase)
        if (finalSearchQuery.trim().toLowerCase() === originalSearchQuery.trim().toLowerCase()) {
            return;
        }

        // reset viewedResults[]
        setViewedResults([]);
        
        // process the search query
        processSearchQuery(finalSearchQuery);

        // enable loading screen
        enableLoadingScreen();

        // effort heuristic – hard-coded delay (processing time)
        await delay(estimatedSearchTimeMilliseconds);

        // Exit condition: CurrentUser left /search page while processing.
        if (!isPageActiveRef.current) { return }; // prevent re-direct

        // redirect user to /results page.
        navigate('/results', { state: {finalSearchQuery} });

        // reset results filter to 'all'
        setResultsFilter('all');

        // disableLoadingScreen when results are ready
        // disableLoadingScreen();
    }


    // ==============
    // INDEX TRACKING – used to track which Document result is opened in the index overlay.
    // ==============
    const [openedEntityIndex, setOpenedEntityIndex] = useState(null);
    const [maxEntityIndex, setMaxEntityIndex] = useState(rankedQueryResults.length);

    // update max index var when filter/rankedQueryResults changes
    useEffect(() => {
        setMaxEntityIndex(rankedQueryResults.length ?? 0);        
    }, [resultsFilter, rankedQueryResults]);
    // handleEntityOpen(id: String) – opens the Document with input UUID (String)
    const handleEntityOpen = (id) => { 
        
        // trigger toast
        cueToast();

        // get the index of the clicked Document
        const clickedIndex = rankedQueryResults.findIndex(doc_json => doc_json.id === id);

        // ⚠️ developer debugging
        // console.log(`SKYBASE (client): Opened Document at Index #${clickedIndex}`);

        // update state var
        setOpenedEntityIndex(clickedIndex);
    }
    
    // cueToast() – triggers the Toast popover
    const [toastKey, setToastKey] = useState(null); // Default: NULL
    const cueToast = () => {
        
        // new unique key → trigger re-render → restart animation
        setToastKey(Date.now());
    };

    // createResultIndexToastText() – creates the Toast popover text in real-time when Toast popover is triggered
    const createResultIndexToastText = () => {
        const filterText = {
            all: "Result",
            keyword: "Keyword Result",
            semantic: "Semantic Result",
        }
        
        return `${filterText[resultsFilter]} ${openedEntityIndex + 1} of ${maxEntityIndex}`
    }


    // ====================
    // KEYDOWN INTERACTIONS – Handle specific keyboard input events
    // ====================
    const handleKeyDown = (event) => {       

        // map 'L' and 'R' arrow keys
        if (event.key === "ArrowRight") {
            handleResultIndexIncrement();
        } else if (event.key === "ArrowLeft") {
            handleResultIndexDecrement();
        }

        // map 'TAB' key
        if (event.key === "Tab") {
            // prevent browser from default focus moving
            event.preventDefault();

            // Round Robin the resultsFilter
            const next = {
                all: "keyword",
                keyword: "semantic",
                semantic: "all",
            };

            setResultsFilter(next[resultsFilter]);
        }
    };

    
    // ============================
    // handleResultIndexIncrement() – IFF YES isDocumentPreviewModalOpen, increments the result index. Avoids IndexOutOfBoundsError's.
    // ============================
    const handleResultIndexIncrement = () => {
        
        // If !isDocumentPreviewModalOpen –> immediately return (prevents errors)
        if (!isDocumentPreviewModalOpen) { return; };
        
        // Store the oldIndex and newIndex.
        const oldIndex = openedEntityIndex;
        const newIndex = oldIndex + 1;

        // Determine if openedEntityIndex can be incremented (no IndexOutOfBoundsError).
        if ( oldIndex + 1 < rankedQueryResults.length) {
            
            // safely increment
            setOpenedEntityIndex((prev) => prev + 1);

            // share new index w. CurrentUser
            cueToast();

            // ⚠️ developer debugging
            // console.log(`SKYBASE (client): Safely INCREMENTED from Index #${oldIndex} to Index #${newIndex}.`);
        } else {
            
            // ⚠️ developer debugging
            // console.log(`SKYBASE (client): IndexOutOfBoundsError – Cannot INCREMENT from Index #${oldIndex} to out-of-bounds Index #${newIndex}.`);

            // cannot increment –> return early
            return;
        }

        // Change entity modals
        const oldId = rankedQueryResults[oldIndex].id; // we know this exists (b/c we just moved from it)
        const newId = rankedQueryResults[newIndex].id;
        changeEntityModal(oldId, newId)
    };


    // ============================
    // handleResultIndexDecrement() – IFF YES isDocumentPreviewModalOpen, decrements the result index. Avoids IndexOutOfBoundsError's.
    // ============================
    const handleResultIndexDecrement = () => {        
        // Store the oldIndex and newIndex.
        const oldIndex = openedEntityIndex;
        const newIndex = oldIndex - 1;

        // Determine if openedEntityIndex can be incremented (no IndexOutOfBoundsError).
        if ( oldIndex - 1 >= 0) {
            
            // safely decrement
            setOpenedEntityIndex((prev) => prev - 1);
            
            // share new index w. CurrentUser
            cueToast();
            
            // ⚠️ developer debugging
            // console.log(`SKYBASE (client): Safely DECREMENTED from Index #${oldIndex} to Index #${newIndex}.`);
        } else {
            
            // ⚠️ developer debugging
            // console.log(`SKYBASE (client): IndexOutOfBoundsError – Cannot DECREMENT from Index #${oldIndex} to out-of-bounds Index #${newIndex}.`);

            // cannot decremennt –> return early
            return;
        }

        // Change entity modals
        const oldEntityUUID = rankedQueryResults[oldIndex].id; // we know this exists (b/c we just moved from it)
        const newEntityUUID = rankedQueryResults[newIndex].id;
        changeEntityModal(oldEntityUUID, newEntityUUID)
    };
    

    // ===============================================
    // changeEntityModal(oldId: String, newId: String) – Simultaneously closes an old DocumentPreviewModal and opens a new DocumentPreviewModal.
    // ===============================================
    const changeEntityModal = (oldId, newId) => {
        // ⚠️ developer debugging
        // console.log(`SKYBASE (client): Old UUID: ${oldId}`);
        // console.log(`SKYBASE (client): New UUID: ${newId}`); 
        
        // close old Document
        handleDocumentClose();

        // open new Document
        openDocumentPreviewModal(newId);
    }


    // =========================================
    // onIndexCounterInstanceClick(uuid: String) – adds the Document UUID to 'viewedResults' (UUID[]), then opens the Document UUID in the DocumentPreviewModal
    // =========================================
    const onIndexCounterInstanceClick = (uuid) => {

        // Add this result to viewedResults, IFF not already added
        if (!viewedResults.includes(uuid)) { setViewedResults(prev => [...prev, uuid]); }

        // open this Document UUID in the DocumentPreviewModal
        openDocumentPreviewModal(uuid);
    }  
    

 
    // JSX Componrnt
    return(
        <div className='Layout'>
            
            {/* Sidebar */}
            <Sidebar isHamburgerAtBottom={!isDesktop}/>

            {/* Main content */}
            <div className='Results' tabIndex={0} onKeyDown={handleKeyDown}>
                
                {/* State: NOT isLoading */}
                { !isSearching && 
                    <>
                        {/* Advanced search bar */}
                        <div className='results-advanced-search-bar'>
                            <form 
                                className='results-advanced-search-bar-form'
                                id="search-form"
                                onSubmit={handleFormSubmit}>
                                <img src={ SearchIconTransparent } className='search-advanced-search-bar-form__search-icon' alt='Search Icon.'/>
                                <input
                                    ref={inputRef}
                                    className='results-advanced-search-bar-form__input'
                                    placeholder={originalSearchQuery}
                                    value={searchQuery}
                                    onChange={(e) => handleFormChange(e)}
                                />
                            </form>
                            { rankedQueryResults.length > 0 && 
                                <div className='results-time-saved-statement'>
                                        <CountUp
                                            start={0}
                                            end={numTotalResults}
                                            duration={1.0}
                                            separator=","
                                        /> Results Found
                                </div>
                            }
                        </div>

                        {/* Repeater Slider */}
                        <div className='ResultsPage-slider'>
                            {/* -- All Filter -- */}
                            <div 
                                className={`ResultsPage-slider-button ${ resultsFilter === "all" ? 'enabled' :''}`}
                                onClick={() => setResultsFilter('all')}>
                                All { numTotalResults > 0  && 
                                    <>
                                        ({(numTotalResults > 0) ? 
                                            <CountUp
                                                start={0}
                                                end={numTotalResults}
                                                duration={1.0}
                                                separator=","
                                            /> : ''
                                        })
                                    </>
                                }
                            </div>


                            {/* -- Keyword Filter -- */}
                            <div 
                                id='note-filter-button'
                                className={`ResultsPage-slider-button ${ resultsFilter === "keyword" ? 'enabled' :''}`}
                                onClick={() => setResultsFilter('keyword')}>
                                Keyword { numKeywordExactResults > 0  && 
                                    <>
                                        (<CountUp
                                                start={0}
                                                end={numKeywordExactResults}
                                                duration={1.0}
                                                separator=","
                                            />)
                                    </>
                                }
                            </div>

                            {/* -- Semantic Filter -- */}
                            <div 
                                className={`ResultsPage-slider-button ${ resultsFilter === "semantic" ? 'enabled' :''}`}
                                onClick={() => setResultsFilter('semantic')}>
                                Contextual { numSemanticResults > 0  && 
                                    <>
                                        (<CountUp
                                                start={0}
                                                end={numSemanticResults}
                                                duration={1.0}
                                                separator=","
                                            />)
                                    </>
                                }
                            </div>



                        </div>

                        {/* Results repeater */}

                        {/* CONDITION: !isRepeaterEmpty */}
                        { !isRepeaterEmpty && <>
                            {/* CONDITION: All results */}
                            { resultsFilter === 'all' && 
                                <>
                                <Virtuoso
                                    className='results-search-results-repeater'
                                    data={rankedQueryResults}

                                    itemContent={(index, result_json) => (
                                        
                                        // Because this only mounts when visible, the useEffect 
                                        // inside FullResultDoc will only fire for visible items.
                                            <span
                                                key={result_json.id}
                                                onClick={() => openDocumentPreviewModal(result_json.id)}
                                                className='ResultsPage-search-results-repeater-instance'
                                                style={{ "--stagger-index": Math.min(index, 0) }}>
                                                <FullResultDoc
                                                    key={result_json.id}
                                                    uuid={result_json.id}
                                                    onOpenCallback={() => handleEntityOpen(result_json.id)}
                                                    onCloseCallback={handleDocumentClose}
                                                    isReadOnly={false}/>
                                            </span>
                                    )}
                                    
                                    // Artificially expands the viewport by 5000px top and bottom
                                    increaseViewportBy={{ top: 5000, bottom: 5000 }}
                                />
                                <div className='results-search-results-repeater-instance'></div>
                                <div className='results-search-results-repeater-instance'></div>
                                <div className='results-search-results-repeater-instance'></div>
                                <div className='results-search-results-repeater-instance'></div>
                                </>
                            }

                            {/* CONDITION: Keyword-only results */}
                            { (resultsFilter === "keyword" && numKeywordExactResults > 0) && 
                                <>
                                <Virtuoso
                                    className='results-search-results-repeater'
                                    data={rankedQueryResults}

                                    itemContent={(index, result_json) => (

                                        <React.Fragment key={result_json.id}>
                                            { (result_json.tags.includes("title-keyword-exact")) && 
                                                
                                                <span
                                                    key={result_json.id}
                                                    onClick={() => openDocumentPreviewModal(result_json.id)}
                                                    className='ResultsPage-search-results-repeater-instance'
                                                    style={{ "--stagger-index": Math.min(index, 0) }}>
                                                    <FullResultDoc
                                                        key={result_json.id}
                                                        uuid={result_json.id}
                                                        onOpenCallback={() => handleEntityOpen(result_json.id)}
                                                        onCloseCallback={handleDocumentClose}/>
                                                </span>
                                            }
                                        </React.Fragment>
                                    )}
                                    
                                    // Artificially expands the viewport by 5000px top and bottom
                                    increaseViewportBy={{ top: 5000, bottom: 5000 }}
                                />
                                <div className='results-search-results-repeater-instance'></div>
                                <div className='results-search-results-repeater-instance'></div>
                                <div className='results-search-results-repeater-instance'></div>
                                <div className='results-search-results-repeater-instance'></div>
                                </>
                            }
                            

                            {/* CONDITION: Semantic results */}
                            { resultsFilter === "semantic" && 
                            <>
                                <Virtuoso
                                    className='results-search-results-repeater'
                                    data={rankedQueryResults}

                                    itemContent={(index, result_json) => (

                                        <React.Fragment key={result_json.id}>
                                            { (result_json.tags.includes("title-semantic-high") ||
                                                result_json.tags.includes("subsection-semantic-high")) && 
                                                <span
                                                    key={result_json.id}
                                                    onClick={() => openDocumentPreviewModal(result_json.id)}
                                                    className='ResultsPage-search-results-repeater-instance'
                                                    style={{ "--stagger-index": Math.min(index, 0) }}>
                                                    <FullResultDoc
                                                        key={result_json.id}
                                                        uuid={result_json.id}
                                                        onOpenCallback={() => handleEntityOpen(result_json.id)}
                                                        onCloseCallback={handleDocumentClose}/>
                                                </span>
                                            }
                                        </React.Fragment>
                                    )}
                                    
                                    // Artificially expands the viewport by 5000px top and bottom
                                    increaseViewportBy={{ top: 5000, bottom: 5000 }}
                                />
                                <div className='results-search-results-repeater-instance'></div>
                                <div className='results-search-results-repeater-instance'></div>
                                <div className='results-search-results-repeater-instance'></div>
                                <div className='results-search-results-repeater-instance'></div>
                                </>
                            }
                        </>
                    }

                    {/* Edge case: YES isRepeaterOnly */}
                    { isRepeaterEmpty && 
                        <div className='results-search-results-repeater'>
                            <div className='results-search-results-repeater--no-results-statement'>
                                { resultsFilter === 'all' && <>No documents matched your search query.</>}
                                { resultsFilter === 'keyword' && <>No documents contain your search keyword.</>}
                                { resultsFilter === 'semantic' && <>No documents are related to your search query.</>}
                            </div>
                        </div>
                    }


                        

                        {/* Toast popover */}
                        { toastKey && 
                            <ResultIndexOverlay
                                key={toastKey}
                                text={createResultIndexToastText()}/>
                        }

                        {/* Index counter overlay */}
                        { isDesktop && 
                            <ResultIndexCounterOverlay
                                openedEntityIndex={openedEntityIndex}                               // the current openedEntityIndex
                                viewedResults={viewedResults}                                       // all viewedResults
                                onChipClickCallback={(uuid) => onIndexCounterInstanceClick(uuid)}   // callback function to trigger upon a chip click
                            />
                        }

                        {/* DocumentPreviewModal */}
                        <FullScreenModal
                            isOpen={(isDocumentPreviewModalOpen)}
                            onClose={handleDocumentClose}
                            children={
                                <DocumentPreviewModal
                                    uuid={targetDocumentUUID}
                                    isReadOnly={true}
                                    isNavigationEnabled={true}
                                    leftNavButtonClickCallback={() => handleResultIndexDecrement()}
                                    rightNavButtonClickCallback={() => handleResultIndexIncrement()}
                                    triggerClose={() => setIsDocumentPreviewModalOpen(false)}
                                />
                            }
                    />
                    </>
                }

                {/* Loading screen overlay */}
                {isSearching &&
                    <LoadingScreenOverlay
                        messagesToDisplay={[
                            "Analyzing search query",
                            "Extracting search vectors",
                            "Searching across database",
                            "Locating nearby vectors",
                            "Scanning indexed vectors",
                            "Evaluating distance metrics",
                            "Calculating similarity estimates",
                            "Accessing query relevance",
                            "Estimating match confidence",
                            "Processing search results",
                            "Filtering candidate results",
                            "Aggregating relevance scores",
                            "Ranking search results",
                            "Ranking top candidates",
                            ]}
                        estimatedTimeMilliseconds={estimatedSearchTimeMilliseconds}
                        currentActionMessage={`Now searching ${activeEngineJson?.name} for`}
                        optionalActionMessage={`"${activeQuery}"`}
                        isLoadingBarFull={isFull}
                    />
                }

            </div>

            {/* InformationalPopover */}
            { isInformationalPopoverOpen &&
                <InformationalPopover
                    titleText={"Subscription Required"}
                    bodyText={"Please purchase a Skybase subscription plan to continue."}
                    primaryButtonText={"View Subscription Plans"}
                    primaryButtonCallback={() => navigate('../pricing')}
                    secondaryButtonText={"Return Home"}
                    secondaryButtonCallback={() => navigate('../')}
                />
            }
        </div>
    );
}


// Export statement
export default Results;